import { useEffect, useCallback, type MutableRefObject } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { OrderFormValues } from '../pages/NewOrder/order.schema';
import type { IStore } from '../interfaces/Store';
import { useCustomerDisplay } from './CustomerDisplayProvider';
import { buildWebDisplayContent } from './orderContent';
import { useGetStores } from '../hooks/stores/queries';
import type { OrderTotalsResult } from '../pages/NewOrder/utils/orderTotals';

export function useOrderDisplay(
  methods: Pick<UseFormReturn<OrderFormValues>, 'getValues' | 'watch'>, store: IStore | undefined,
  brand: { name?: string; logoUrl?: string; country?: unknown },
  calculatedDisplayTotals?: MutableRefObject<OrderTotalsResult['displayTotals']>
) {
  const { session } = useCustomerDisplay();
  const { name, logoUrl, country } = brand;
  const { data: stores } = useGetStores();
  const storeCountry = stores?.find((candidate) => candidate._id === store?._id)?.country || store?.country;
  useEffect(() => {
    if (!session || !store) return;
    let timer: ReturnType<typeof setTimeout>;
    const publishCalculated = () => {
      // A single end-of-turn snapshot coalesces RHF's multiple setValue/reset notifications.
      clearTimeout(timer);
      timer = setTimeout(() => {
        const values = methods.getValues();
        session.setDraft(buildWebDisplayContent({ ...values, displayTotals: calculatedDisplayTotals?.current }, { ...store, country: storeCountry }, { name, logoUrl, country }));
      }, 0);
    };
    publishCalculated();
    const subscription = methods.watch(publishCalculated);
    return () => {
      clearTimeout(timer);
      subscription.unsubscribe();
      session.clear();
    };
  }, [session, methods, store, storeCountry, name, logoUrl, country, calculatedDisplayTotals]);
  return useCallback(() => session?.activity(), [session]);
}
